import { useState, useMemo } from "react";
import { Representee } from "api/common/useGetContactInfo";
import { ComboBox, Option } from "components/ComboBox/ComboBox";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";

interface ImpersonationSelectProps {
    representees: Representee[];
    selected: Representee | undefined;
    onSelect: (representee: Representee) => void;
}

export const ImpersonationSelect = ({
    representees,
    selected,
    onSelect,
}: ImpersonationSelectProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const { t } = useModifiedTranslation();

    const options = useMemo(
        () => representees.map((r) => ({ id: r.id, label: r.name })),
        [representees]
    );

    // Filter the list ourselves: ComboBox renders the `options` it is given
    // as-is, so filtering must happen here (mirrors PortfolioSelect).
    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        return options.filter((o) => o.label.toLowerCase().includes(query));
    }, [options, searchQuery]);

    const selectedOption = selected
        ? { id: selected.id, label: selected.name }
        : undefined;

    const handleChange = (option: Option) => {
        const representee = representees.find((r) => r.id === option.id);
        if (representee) onSelect(representee);
    };

    return (
        <div className="min-w-[300px]">
            <ComboBox
                value={selectedOption}
                onChange={handleChange}
                options={filteredOptions}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                label={t("userMenu.switchUserLabel")}
            />
        </div>
    );
};
